/**
 * Publikus egyedi ajánlat oldal — `/egyedi-ajanlat/:token`.
 *
 * Az oldal token alapján lekéri a `GET /api/custom-offers/:token`
 * endpointot, és négy állapotot kezel:
 *   - active   → megjeleníti az árat, számlázási űrlapot, Fizetés gombot
 *   - used     → „Az ajánlat már teljesült"
 *   - expired  → „Az ajánlat lejárt"
 *   - revoked  → „Az ajánlat visszavonásra került"
 *   - 404      → „Az ajánlat nem található vagy már nem érhető el"
 *
 * A fizetés indításakor a szerver a token alapján olvassa a végösszeget —
 * a frontend csak a számlázási adatokat küldi, az árat NEM.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, MapPin, CheckCircle2, Clock, XCircle, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { publicCustomOfferGet, publicCustomOfferStartPay } from '../services/api';
import { GYM_VENUE } from '../config/venue';

const fmtHUF = (n) => (n == null ? '—' : `${Number(n).toLocaleString('hu-HU')} Ft`);
const fmtFull = (iso) => (iso ? new Date(iso).toLocaleString('hu-HU', { dateStyle: 'long', timeStyle: 'short' }) : '—');

const STATE_COPY = {
  used:    { icon: CheckCircle2, color: 'text-blue-600',    title: 'Ez az ajánlat már teljesült',   body: 'Az ajánlathoz tartozó fizetés már megérkezett. Ha kérdésed van, keress bátran.' },
  expired: { icon: Clock,        color: 'text-gray-500',    title: 'Ez az ajánlat lejárt',            body: 'Sajnos ez a személyre szabott ajánlat már nem érhető el. Kérjük, vedd fel a kapcsolatot velünk, és készítek neked egy újat.' },
  revoked: { icon: XCircle,      color: 'text-red-600',     title: 'Ez az ajánlat visszavonásra került', body: 'Az ajánlat visszavonásra került. Kérjük, vedd fel a kapcsolatot velünk.' },
};

export default function CustomOfferPage() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [billing, setBilling] = useState({
    name: '', email: '', address: '', zip: '', city: 'Miskolc', country: 'HU',
  });

  useEffect(() => {
    publicCustomOfferGet(token)
      .then((d) => setOffer(d))
      .catch((e) => setError(e?.response?.data?.detail || 'Az ajánlat nem található vagy már nem érhető el.'))
      .finally(() => setLoading(false));
  }, [token]);

  const set = (k) => (e) => setBilling((b) => ({ ...b, [k]: e.target.value }));

  const pay = async () => {
    for (const f of ['name', 'email', 'address', 'zip', 'city']) {
      if (!billing[f] || String(billing[f]).trim().length < 2) {
        toast.error('Kérjük, töltsd ki az összes számlázási mezőt.');
        return;
      }
    }
    setPaying(true);
    try {
      const r = await publicCustomOfferStartPay(token, billing);
      if (r.gateway_url) {
        window.location.href = r.gateway_url;
      } else {
        toast.error('Nem sikerült a fizetést indítani.');
        setPaying(false);
      }
    } catch (e) {
      toast.error('Hiba: ' + (e?.response?.data?.detail || e.message));
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-yellow-600" size={32} />
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center" data-testid="offer-not-found">
          <XCircle className="text-gray-400 mx-auto mb-4" size={40} />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Az ajánlat nem érhető el</h1>
          <p className="text-gray-600 text-sm">{error || 'Az ajánlat nem található vagy már nem érhető el.'}</p>
          <a href="/" className="mt-6 inline-block text-yellow-600 font-semibold hover:underline">Vissza a főoldalra →</a>
        </div>
      </div>
    );
  }

  const inactive = offer.status !== 'active';
  const stateCopy = STATE_COPY[offer.status];

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-xl mx-auto">
        {/* Brand mini-header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#273D5C] flex items-center justify-center text-yellow-400 font-bold">OC</div>
            <div className="text-left">
              <div className="font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>OROSZ COACHING</div>
              <div className="text-xs text-gray-500">Személyre szabott ajánlat</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="offer-card">
          <div className="bg-gradient-to-br from-[#273D5C] to-[#1e2f47] px-6 py-8 text-white">
            <div className="text-xs uppercase tracking-widest text-yellow-400/90 mb-2">Egyedi ajánlat neked</div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>{offer.title}</h1>
            {!inactive && (
              <div className="mt-4 text-4xl font-bold text-yellow-400" data-testid="offer-price">{fmtHUF(offer.custom_price)}</div>
            )}
            {!inactive && offer.expires_at && (
              <div className="mt-3 text-xs text-white/70">Érvényes: <span className="font-semibold text-white">{fmtFull(offer.expires_at)}</span>-ig</div>
            )}
          </div>

          {inactive && stateCopy && (
            <div className="p-8 text-center" data-testid={`offer-state-${offer.status}`}>
              <stateCopy.icon className={`${stateCopy.color} mx-auto mb-4`} size={40} />
              <h2 className="text-lg font-bold text-gray-900 mb-2">{stateCopy.title}</h2>
              <p className="text-sm text-gray-600 leading-relaxed">{stateCopy.body}</p>
              <a href="mailto:info@oroszcoaching.hu" className="mt-6 inline-block text-yellow-600 font-semibold hover:underline">Kapcsolat →</a>
            </div>
          )}

          {!inactive && (
            <div className="p-6 space-y-5">
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 leading-relaxed">
                <span className="font-semibold">Fontos:</span> A feltüntetett ár az{' '}
                <span className="font-semibold">edzői mentorálás</span> díja. A{' '}
                <MapPin size={11} className="inline text-amber-700" />{' '}
                <span className="font-semibold">{GYM_VENUE.name}</span> terembe való belépés / terembérlet{' '}
                <span className="font-semibold">nem része az árnak</span> — azt a résztvevő a helyszínen, külön rendezi.
              </div>

              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Számlázási adatok</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <Label className="text-xs text-gray-600">Név *</Label>
                    <Input data-testid="billing-name" value={billing.name} onChange={set('name')} placeholder="Kiss János" className="mt-1" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-gray-600">Email *</Label>
                    <Input data-testid="billing-email" type="email" value={billing.email} onChange={set('email')} placeholder="kiss@example.com" className="mt-1" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-gray-600">Cím *</Label>
                    <Input data-testid="billing-address" value={billing.address} onChange={set('address')} placeholder="Fő utca 12." className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Irányítószám *</Label>
                    <Input data-testid="billing-zip" value={billing.zip} onChange={set('zip')} placeholder="3526" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Város *</Label>
                    <Input data-testid="billing-city" value={billing.city} onChange={set('city')} placeholder="Miskolc" className="mt-1" />
                  </div>
                </div>
              </div>

              <Button
                onClick={pay}
                disabled={paying}
                data-testid="offer-pay-btn"
                className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold h-12 text-base"
              >
                {paying ? (
                  <><Loader2 className="animate-spin mr-2" size={18} /> Átirányítás Barionra…</>
                ) : (
                  <>Fizetés bankkártyával — {fmtHUF(offer.custom_price)}</>
                )}
              </Button>

              <div className="flex items-center justify-center gap-1.5 text-[11px] text-gray-500">
                <ShieldCheck size={12} className="text-emerald-600" />
                <span>Biztonságos fizetés a <span className="font-semibold text-gray-700">Barion</span> rendszerén keresztül. A kártya adatok a kereskedőhöz nem jutnak el.</span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 text-center text-xs text-gray-500">
          Kérdésed van? Írj a{' '}
          <a href="mailto:info@oroszcoaching.hu" className="text-yellow-600 font-semibold hover:underline">info@oroszcoaching.hu</a> címre.
        </div>
      </div>
    </div>
  );
}
